// Parámetros legales de nómina por año (SBU, % IESS, tabla IR).
// Nómina → Configuración. Solo admin (la RPC exige _es_admin).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, History, Loader2, Plus, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, nomRpc, useDialogoEnLinea, type Parametros as Params, type SbuDivergencia, type TramoIR } from './shared';
import { RevisionSbuModal } from './RevisionSbu';
import { LOCALE } from '@/lib/utils/regional';

interface AuditCambio { campo: string; antes: unknown; despues: unknown }
interface ParamAuditRow { fecha: string; operacion: string; usuario: string; ip: string | null; equipo: string | null; anio: string | null; cambios: AuditCambio[] }
const OP_LABEL: Record<string, string> = { INSERT: 'Creación', UPDATE: 'Modificación', DELETE: 'Eliminación' };
const auditVal = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));

const ANIO_ACTUAL = new Date().getFullYear();

export function Parametros() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [anio, setAnio] = useState(ANIO_ACTUAL);
  const [histAbierto, setHistAbierto] = useState(false);
  const [revisionAbierta, setRevisionAbierta] = useState(false);
  const { data: hist = [] } = useQuery<ParamAuditRow[]>({
    queryKey: ['nom-parametros-auditoria'],
    queryFn: () => nomRpc<ParamAuditRow[]>('nom_parametros_auditoria'),
    enabled: histAbierto,
  });
  const { data, isFetching } = useQuery<Params | null>({
    queryKey: ['nom-parametros', anio],
    queryFn: () => nomRpc<Params | null>('nom_parametros', { _anio: anio }),
  });

  const [form, setForm] = useState({ sbu: '', aporte_personal_pct: '9.45', aporte_patronal_pct: '11.15', iece_pct: '0.5', secap_pct: '0.5', fondos_reserva_pct: '8.33', region_decimo_cuarto: 'sierra', canasta_basica: '', salario_digno: '', subsidio_enfermedad_pct: '75', vacaciones_modo: 'provision', base_calculo: 'mensual', dias_divisor: '30', dias_semana_default: '6', incluir_horas_extra: false, jornal_recargo_pct: '0', codigo_empleado_prefijo: 'EMP-', codigo_empleado_digitos: '4' });
  const [tabla, setTabla] = useState<TramoIR[]>([]);

  useEffect(() => {
    if (data) {
      setForm({
        sbu: String(data.sbu), aporte_personal_pct: String(data.aporte_personal_pct),
        aporte_patronal_pct: String(data.aporte_patronal_pct), iece_pct: String(data.iece_pct),
        secap_pct: String(data.secap_pct), fondos_reserva_pct: String(data.fondos_reserva_pct),
        region_decimo_cuarto: data.region_decimo_cuarto ?? 'sierra',
        canasta_basica: data.canasta_basica != null ? String(data.canasta_basica) : '',
        salario_digno: data.salario_digno != null ? String(data.salario_digno) : '',
        subsidio_enfermedad_pct: data.subsidio_enfermedad_pct != null ? String(data.subsidio_enfermedad_pct) : '75',
        vacaciones_modo: data.vacaciones_modo ?? 'provision',
        base_calculo: data.base_calculo ?? 'mensual',
        dias_divisor: data.dias_divisor != null ? String(data.dias_divisor) : '30',
        dias_semana_default: data.dias_semana_default != null ? String(data.dias_semana_default) : '6',
        incluir_horas_extra: data.incluir_horas_extra ?? false,
        jornal_recargo_pct: data.jornal_recargo_pct != null ? String(data.jornal_recargo_pct) : '0',
        codigo_empleado_prefijo: data.codigo_empleado_prefijo ?? 'EMP-',
        codigo_empleado_digitos: data.codigo_empleado_digitos != null ? String(data.codigo_empleado_digitos) : '4',
      });
      setTabla(data.tabla_ir ?? []);
    } else if (data === null) {
      setTabla([]);
    }
  }, [data]);

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_parametros_guardar', {
      _data: {
        anio, sbu: Number(form.sbu),
        aporte_personal_pct: Number(form.aporte_personal_pct), aporte_patronal_pct: Number(form.aporte_patronal_pct),
        iece_pct: Number(form.iece_pct), secap_pct: Number(form.secap_pct), fondos_reserva_pct: Number(form.fondos_reserva_pct),
        region_decimo_cuarto: form.region_decimo_cuarto,
        canasta_basica: form.canasta_basica || null,
        salario_digno: form.salario_digno || null,
        subsidio_enfermedad_pct: Number(form.subsidio_enfermedad_pct) || 75,
        vacaciones_modo: form.vacaciones_modo,
        base_calculo: form.base_calculo,
        dias_divisor: form.dias_divisor || '30',
        dias_semana_default: form.dias_semana_default || '6',
        incluir_horas_extra: form.incluir_horas_extra,
        jornal_recargo_pct: form.jornal_recargo_pct || '0',
        codigo_empleado_prefijo: form.codigo_empleado_prefijo,
        codigo_empleado_digitos: form.codigo_empleado_digitos || '4',
        tabla_ir: tabla,
      },
    }),
    onSuccess: () => { toast.success(`Parámetros ${anio} guardados`); void qc.invalidateQueries({ queryKey: ['nom-parametros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // El mínimo legal del país es un dato nacional (script 798): la empresa puede
  // estar por encima, nunca por debajo.
  const { data: divergencias = [] } = useQuery<SbuDivergencia[]>({
    queryKey: ['nom-sbu-divergencias'],
    queryFn: () => nomRpc<SbuDivergencia[]>('nom_sbu_divergencias'),
  });
  const divergencia = divergencias.find((d) => d.anio === anio) ?? null;

  const alinear = useMutation({
    mutationFn: () => nomRpc('nom_parametros_alinear_sbu', { _anio: anio, _confirmar: true }),
    onSuccess: (r) => {
      toast.success((r as { mensaje?: string }).mensaje ?? 'Básico alineado');
      void qc.invalidateQueries({ queryKey: ['nom-parametros'] });
      void qc.invalidateQueries({ queryKey: ['nom-sbu-divergencias'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setTramo = (i: number, campo: keyof TramoIR, valor: string) => {
    setTabla((t) => t.map((tr, j) => (j === i ? { ...tr, [campo]: valor === '' ? (campo === 'hasta' ? null : 0) : Number(valor) } : tr)));
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Parámetros de nómina</h1>
        <div className="flex items-center gap-2">
          <Campo label=""><input type="number" className={`${fieldCls} w-28`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" /></Campo>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          <Button variant="outline" onClick={() => setRevisionAbierta(true)}>
            <ClipboardCheck className="w-4 h-4 mr-1" /> Revisar contratos
          </Button>
          <Button variant="outline" onClick={() => setHistAbierto(true)}><History className="w-4 h-4 mr-1" /> Historial de cambios</Button>
        </div>
      </div>

      {/* El básico no es una preferencia de la empresa: lo fija un Acuerdo
          Ministerial. Cuando el parámetro se queda atrás no sólo calcula de
          menos el décimo cuarto — apaga los controles que se miden contra él. */}
      {divergencia && (
        <div className={`rounded-md border p-3 text-sm space-y-2 ${
          divergencia.estado === 'bajo_el_minimo' ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30'}`}>
          <div className="flex items-start gap-2">
            {divergencia.estado === 'bajo_el_minimo'
              ? <ShieldAlert className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
              : <ShieldCheck className="w-4 h-4 mt-0.5 text-ok shrink-0" />}
            <div>
              {divergencia.estado === 'bajo_el_minimo' && (
                <>El salario básico de {anio} está en <b>{Number(divergencia.sbu_empresa).toFixed(2)}</b> y el mínimo
                  legal de {divergencia.pais} es <b>{Number(divergencia.sbu_legal).toFixed(2)}</b>. Faltan{' '}
                  <b>{Math.abs(Number(divergencia.diferencia)).toFixed(2)}</b>.</>
              )}
              {divergencia.estado === 'alineado' && <>El salario básico coincide con el mínimo legal de {divergencia.pais} ({Number(divergencia.sbu_legal).toFixed(2)}).</>}
              {divergencia.estado === 'sobre_el_minimo' && (
                <>La empresa paga <b>{Number(divergencia.diferencia).toFixed(2)}</b> por encima del mínimo legal
                  ({Number(divergencia.sbu_legal).toFixed(2)}). Es legal y no se toca.</>
              )}
              {divergencia.fuente && <div className="text-xs text-muted-foreground mt-1">{divergencia.fuente}</div>}
            </div>
          </div>
          {divergencia.estado === 'bajo_el_minimo' && (
            <Button size="sm" isLoading={alinear.isPending}
              onClick={async () => { if (await dlg.confirmar('¿Subir el básico al mínimo legal?', { detalle: `El básico de ${anio} pasará de ${Number(divergencia.sbu_empresa).toFixed(2)} a ${Number(divergencia.sbu_legal).toFixed(2)}.`, aceptar: 'Subir el básico' })) alinear.mutate(); }}>
              Subirlo al mínimo legal
            </Button>
          )}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Valores legales {anio}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {data === null && <div className="text-sm text-pendiente">No hay parámetros para {anio}: se crearán al guardar.</div>}
          <div className="grid grid-cols-3 gap-3">
            <Campo label="SBU — salario básico unificado ($)"><input type="number" step="0.01" className={fieldCls} value={form.sbu} onChange={(e) => setForm({ ...form, sbu: e.target.value })} /></Campo>
            <Campo label="Aporte personal IESS (%)"><input type="number" step="0.01" className={fieldCls} value={form.aporte_personal_pct} onChange={(e) => setForm({ ...form, aporte_personal_pct: e.target.value })} /></Campo>
            <Campo label="Aporte patronal IESS (%)"><input type="number" step="0.01" className={fieldCls} value={form.aporte_patronal_pct} onChange={(e) => setForm({ ...form, aporte_patronal_pct: e.target.value })} /></Campo>
            <Campo label="IECE (%)"><input type="number" step="0.01" className={fieldCls} value={form.iece_pct} onChange={(e) => setForm({ ...form, iece_pct: e.target.value })} /></Campo>
            <Campo label="SECAP (%)"><input type="number" step="0.01" className={fieldCls} value={form.secap_pct} onChange={(e) => setForm({ ...form, secap_pct: e.target.value })} /></Campo>
            <Campo label="Fondos de reserva (%) — Art. 196 CT"><input type="number" step="0.01" className={fieldCls} value={form.fondos_reserva_pct} onChange={(e) => setForm({ ...form, fondos_reserva_pct: e.target.value })} /></Campo>
            <Campo label="Canasta básica familiar ($ INEC) — tope rebaja IR">
              <input type="number" step="0.01" className={fieldCls} value={form.canasta_basica} onChange={(e) => setForm({ ...form, canasta_basica: e.target.value })} />
            </Campo>
            <Campo label="Salario digno anual ($ MDT) — aviso compensación (COPCI)">
              <input type="number" step="0.01" className={fieldCls} value={form.salario_digno} onChange={(e) => setForm({ ...form, salario_digno: e.target.value })} />
            </Campo>
            <Campo label="Subsidio enfermedad IESS (%) — descuento (Art. 42.19)">
              <input type="number" step="0.01" className={fieldCls} value={form.subsidio_enfermedad_pct} onChange={(e) => setForm({ ...form, subsidio_enfermedad_pct: e.target.value })} />
            </Campo>
            <Campo label="Región décimo cuarto (Art. 113 CT)">
              <select className={fieldCls} value={form.region_decimo_cuarto} onChange={(e) => setForm({ ...form, region_decimo_cuarto: e.target.value })}>
                <option value="sierra">Sierra / Amazonía (ago→jul, pago 15-ago)</option>
                <option value="costa">Costa / Insular (mar→feb, pago 15-mar)</option>
              </select>
            </Campo>
            <Campo label="Vacaciones en rol semanal (Art. 69 CT)">
              <select className={fieldCls} value={form.vacaciones_modo} onChange={(e) => setForm({ ...form, vacaciones_modo: e.target.value })}>
                <option value="provision">Acumular como provisión (no se paga en el neto)</option>
                <option value="neto">Pagar en el neto semanal (1/24)</option>
              </select>
            </Campo>
          </div>
          <p className="text-xs text-muted-foreground">
            SBU según Acuerdo del Ministerio del Trabajo del año; verifica el valor vigente antes de generar roles.
          </p>
          <p className="text-xs text-muted-foreground">
            <b>Vacaciones:</b> el 1/24 de la remuneración (15 días/año, Art. 69). <b>Provisión</b> = se acumula como costo
            y NO se suma al neto (el trabajador toma sus días de descanso). <b>Pagar en el neto</b> = se mensualiza y se
            abona cada semana como los décimos/fondos de reserva; aun así el trabajador conserva el derecho al descanso.
            El cambio aplica al recalcular/cerrar cada rol.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Modelo de cálculo — rol semanal en firme</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Campo label="Base de cálculo del día">
              <select className={fieldCls} value={form.base_calculo} onChange={(e) => setForm({ ...form, base_calculo: e.target.value })}>
                <option value="mensual">Mensual (sueldo ÷ divisor)</option>
                <option value="jornal">Jornal (por día trabajado)</option>
              </select>
            </Campo>
            <Campo label="Divisor mensual → día">
              <input type="number" step="1" className={fieldCls} value={form.dias_divisor} onChange={(e) => setForm({ ...form, dias_divisor: e.target.value })} />
            </Campo>
            <Campo label="Días que paga una semana completa">
              <input type="number" step="1" min="1" max="7" className={fieldCls} value={form.dias_semana_default} onChange={(e) => setForm({ ...form, dias_semana_default: e.target.value })} />
            </Campo>
            <Campo label="Horas extra en el rol semanal">
              <select className={fieldCls} value={form.incluir_horas_extra ? 'si' : 'no'} onChange={(e) => setForm({ ...form, incluir_horas_extra: e.target.value === 'si' })}>
                <option value="no">No calcular</option>
                <option value="si">Sí — suplementarias/extraordinarias/nocturnas (desde novedades)</option>
              </select>
            </Campo>
            <Campo label="Recargo del jornal (%) — Contrato Especial Discontinuo Bananero">
              <input type="number" step="0.01" min="0" max="100" className={fieldCls} value={form.jornal_recargo_pct} onChange={(e) => setForm({ ...form, jornal_recargo_pct: e.target.value })} />
            </Campo>
            <Campo label="Código de empleado — prefijo">
              <input className={fieldCls} value={form.codigo_empleado_prefijo} onChange={(e) => setForm({ ...form, codigo_empleado_prefijo: e.target.value })} placeholder="EMP-" />
            </Campo>
            <Campo label="Código de empleado — n.º de dígitos">
              <input type="number" min="1" max="12" step="1" className={fieldCls} value={form.codigo_empleado_digitos} onChange={(e) => setForm({ ...form, codigo_empleado_digitos: e.target.value })} />
            </Campo>
            <Campo label="Vista previa del código">
              <div className={`${fieldCls} flex items-center font-mono bg-muted/40`}>
                {(form.codigo_empleado_prefijo ?? '') + '1'.padStart(Math.min(Math.max(Number(form.codigo_empleado_digitos) || 4, 1), 12), '0')}
              </div>
            </Campo>
          </div>
          <p className="text-xs text-muted-foreground">
            <b>Jornal diario</b> = sueldo ÷ divisor. Con <b>30</b> se paga el descanso semanal como día calendario
            (poner "días que paga una semana completa" = <b>7</b>); con ~<b>26</b> (días laborables) el descanso queda
            incorporado en la tarifa pagando 6 días. El descanso obligatorio (Art. 50-52) debe quedar remunerado en
            cualquiera de los dos esquemas. Si activas horas extra, el motor lee las novedades del período y aplica
            +50% (suplementarias), +100% (extraordinarias/nocturnas 22h-06h) y +25% (recargo nocturno), Art. 49/55,
            e integra esos recargos a las bases de décimos, fondos de reserva, vacaciones e IESS (Art. 95).
            El <b>recargo del jornal</b> (0 = desactivado) aplica el % del Contrato Especial Discontinuo Bananero
            (típ. 15%) sobre la tarifa diaria y el valor hora; actívalo solo si la empresa opera bajo ese contrato.
          </p>
          <p className="text-xs text-muted-foreground">
            <b>Código de empleado:</b> el prefijo + n.º de dígitos definen la máscara del código que se asigna al crear
            un empleado (secuencial por empresa). Cambiarlo afecta solo a los <b>nuevos</b> empleados; no re-numera los existentes.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Tabla de impuesto a la renta (LORTI Art. 36 — anual)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fracción básica desde ($)</th><th className="py-2 pr-3">Exceso hasta ($)</th>
              <th className="py-2 pr-3">Impuesto fracción básica ($)</th><th className="py-2 pr-3">% sobre exceso</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {tabla.map((t, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1 pr-3"><input type="number" step="0.01" className={fieldCls} value={t.desde} onChange={(e) => setTramo(i, 'desde', e.target.value)} aria-label={`Desde tramo ${i + 1}`} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" className={fieldCls} value={t.hasta ?? ''} placeholder="En adelante" onChange={(e) => setTramo(i, 'hasta', e.target.value)} aria-label={`Hasta tramo ${i + 1}`} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" className={fieldCls} value={t.imp_basico} onChange={(e) => setTramo(i, 'imp_basico', e.target.value)} aria-label={`Impuesto tramo ${i + 1}`} /></td>
                  <td className="py-1 pr-3"><input type="number" step="0.01" className={fieldCls} value={t.pct_exceso} onChange={(e) => setTramo(i, 'pct_exceso', e.target.value)} aria-label={`Porcentaje tramo ${i + 1}`} /></td>
                  <td className="py-1 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Quitar tramo" onClick={() => setTabla((tb) => tb.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {tabla.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin tramos: agrega la tabla del SRI vigente</td></tr>}
            </tbody>
          </table>
          <Button variant="outline" onClick={() => setTabla((t) => [...t, { desde: 0, hasta: null, imp_basico: 0, pct_exceso: 0 }])}>
            <Plus className="w-4 h-4 mr-1" /> Agregar tramo
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} disabled={!Number(form.sbu)} onClick={() => guardar.mutate()}>Guardar parámetros {anio}</Button>
      </div>

      {histAbierto && (
        <Modal title="Historial de cambios — Parámetros de nómina" onClose={() => setHistAbierto(false)} wide>
          {hist.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin cambios registrados.</p>
          ) : (
            <div className="max-h-[70vh] space-y-3 overflow-y-auto">
              {hist.map((a, i) => (
                <div key={i} className="rounded-md border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{OP_LABEL[a.operacion] ?? a.operacion}{a.anio ? ` · año ${a.anio}` : ''}</span>
                    <span className="text-xs text-muted-foreground">{new Date(a.fecha).toLocaleString(LOCALE)}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Usuario: <b className="text-foreground">{a.usuario}</b>
                    {a.ip && <> · IP: {a.ip}</>}
                    {a.equipo && <> · <span title={a.equipo}>{a.equipo.length > 48 ? a.equipo.slice(0, 48) + '…' : a.equipo}</span></>}
                  </div>
                  {a.cambios.length > 0 && (
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Campo</th><th className="py-1 pr-3">Antes</th><th className="py-1">Después</th></tr></thead>
                        <tbody>
                          {a.cambios.map((c, j) => (
                            <tr key={j} className="border-t border-border/60">
                              <td className="py-1 pr-3 font-medium">{c.campo}</td>
                              <td className="py-1 pr-3 text-muted-foreground line-through">{auditVal(c.antes)}</td>
                              <td className="py-1">{auditVal(c.despues)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      {revisionAbierta && <RevisionSbuModal anio={anio} onClose={() => setRevisionAbierta(false)} />}
    </div>
  );
}
